// Pretty-prints a (usually single-line) SQL string into multi-line "paragraph"
// form for readability. Insertion-based: it only adds line breaks at clause
// boundaries, top-level commas, and top-level AND/OR, preserving the query's
// own inter-token spacing. Quote- and paren-aware, so it never breaks inside a
// string literal, a quoted identifier, or a function-call argument list.

const INDENT = "    ";

// Keywords that begin a new top-level line at column 0.
const CLAUSE = new Set([
    "SELECT", "FROM", "WHERE", "GROUP", "ORDER", "HAVING", "LIMIT", "OFFSET",
    "UNION", "JOIN", "LEFT", "RIGHT", "INNER", "FULL", "CROSS",
]);

// Words that are part of a multi-word JOIN phrase; while in one, a following
// JOIN/OUTER must not trigger another break (so "LEFT JOIN" stays on one line).
const JOIN_CONT = new Set(["LEFT", "RIGHT", "INNER", "FULL", "CROSS", "OUTER", "JOIN"]);

const isWordChar = (c: string): boolean => /[A-Za-z0-9_]/.test(c);

export function formatSql(sql: string): string {
    const text = sql.trim();
    if (!text) {
        return text;
    }

    let out = "";
    let depth = 0;
    let prevWord = "";
    let i = 0;
    const n = text.length;
    const trimEnd = () => {
        out = out.replace(/[ \t]+$/, "");
    };

    while (i < n) {
        const c = text[i];

        // String literal / quoted identifier: copy verbatim to the close quote
        // (handling the doubled-quote escape) so its contents are never parsed.
        if (c === "'" || c === '"') {
            out += c;
            i++;
            while (i < n) {
                out += text[i];
                if (text[i] === c) {
                    if (text[i + 1] === c) {
                        out += text[i + 1];
                        i += 2;
                        continue;
                    }
                    i++;
                    break;
                }
                i++;
            }
            continue;
        }

        if (c === "(") {
            depth++;
            out += c;
            i++;
            continue;
        }
        if (c === ")") {
            depth = Math.max(0, depth - 1);
            out += c;
            i++;
            continue;
        }

        // Top-level comma: end the line and indent the next item.
        if (c === "," && depth === 0) {
            trimEnd();
            out += ",\n" + INDENT;
            i++;
            while (i < n && text[i] === " ") {
                i++;
            }
            continue;
        }

        if (isWordChar(c)) {
            let j = i;
            while (j < n && isWordChar(text[j])) {
                j++;
            }
            const word = text.slice(i, j);
            const upper = word.toUpperCase();

            if (depth === 0 && (upper === "AND" || upper === "OR")) {
                trimEnd();
                out += "\n" + INDENT + word;
            } else if (
                depth === 0 &&
                CLAUSE.has(upper) &&
                !(JOIN_CONT.has(prevWord) && JOIN_CONT.has(upper))
            ) {
                if (out.replace(/\s+$/, "").length > 0) {
                    trimEnd();
                    out += "\n";
                }
                out += word;
                // Stack the SELECT list: each column on its own indented line.
                if (upper === "SELECT") {
                    i = j;
                    while (i < n && text[i] === " ") {
                        i++;
                    }
                    out += "\n" + INDENT;
                    prevWord = upper;
                    continue;
                }
            } else {
                out += word;
            }
            prevWord = upper;
            i = j;
            continue;
        }

        out += c;
        i++;
    }

    return out;
}

// render_response prefixes the query block with a bold "**SQL**" label. The chat
// renders its own collapsible "SQL" toggle on the block, so drop the duplicate
// label that immediately precedes a ```sql fence.
export function stripSqlHeading(content: string): string {
    return content.replace(/\*\*SQL\*\*\s*(```sql)/i, "$1");
}

// render_md_table appends a trailing "_(showing N of M rows)_" footer when the
// markdown table is truncated to its preview cap. The chat surfaces that count
// next to the Download CSV control instead, so lift it out of the rendered
// markdown: return the body with the footer removed plus the bare
// "showing N of M rows" text (note is null when there is no footer).
export function extractRowCountNote(content: string): { content: string; note: string | null } {
    const match = content.match(/\n*_\((showing [\d,]+ of [\d,]+ rows)\)_\s*$/i);
    if (!match) {
        return { content, note: null };
    }
    return { content: content.slice(0, match.index).replace(/\s+$/, ""), note: match[1] };
}

// Split markdown content into text before the table and the table itself.
// Markdown tables start with a header row like `| col1 | col2 |` followed by
// a separator `| --- | --- |`. Returns { textBefore, tableMd } where tableMd
// is empty when there is no table.
export function splitAtTable(content: string): { textBefore: string; tableMd: string } {
    const match = content.match(/(^|\n)(\|[^\n]+\|\s*\n\|[\s:|-]+\|\s*\n)/);
    if (!match || match.index === undefined) {
        return { textBefore: content, tableMd: "" };
    }
    const tableStart = match.index + match[1].length;
    return {
        textBefore: content.slice(0, match.index).replace(/\s+$/, ""),
        tableMd: content.slice(tableStart),
    };
}

// render_response lays out the answer as intro -> result table -> "**SQL**"
// block -> optional investigation steps. Split it just before the SQL block so
// the view can render the table, then the Download CSV control, then the SQL
// block - keeping the download directly under the table. Returns the whole
// content as `beforeSql` (and an empty `sqlBlock`) when there is no SQL block,
// e.g. clarifications and errors.
export function splitBeforeSqlBlock(content: string): { beforeSql: string; sqlBlock: string } {
    const match = content.match(/\*\*SQL\*\*\s*```sql/i);
    if (!match || match.index === undefined) {
        return { beforeSql: content, sqlBlock: "" };
    }
    return {
        beforeSql: content.slice(0, match.index).replace(/\s+$/, ""),
        sqlBlock: content.slice(match.index)
    };
}
